/* ==========================================================================
   ADA / WCAG 2.1 AA accessibility layer
   Handsome Scent — August 2026

   DESIGN RULE: this file changes ARIA, focus and announcements ONLY.
   It never changes what a control does, never blocks an event, never
   alters prices, cart state or layout. Nothing here can affect conversion.
   Every patch fails safe: if its target is absent, it does nothing.

   Patches
     1  Video carousel dots      accessible names + aria-current      4.1.2
     2  Video carousel cards     keyboard operable                    2.1.1
     3  Video carousel track     focusable scroll region              2.1.1
     4  Video modal              dialog semantics + focus trap        4.1.2 2.4.3
     5  Announcement marquee     real pause control                   2.2.2
     6  Drawers                  dialog semantics, Escape             4.1.2 2.4.3
     7  Untitled iframes         accessible names                     4.1.2
     8  Rebuild focus + live     bundle builders, collection grid     2.4.3 4.1.3
     9  Bundle builder controls  names, roles, keyboard               2.1.1 4.1.2
    10  Modals/popups            dialog semantics + focus trap        4.1.2 2.4.3
    11  Collection + facets      swatches, Clear all, filter names    2.1.1 4.1.2
    12  Inline <style> leak      move out of links/headings           1.3.1

   Sept 2026: patches 8, 9 and 10 extended to the September bundle builder
   (#hs-sept-wrap) and the 30ml builder (#hs30-wrap-*). Popups that are
   re-rendered on every interaction are re-attached after each rebuild.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Is the user driving from the keyboard right now? Focus is only ever
     restored for keyboard users — a mouse user must never be scrolled or
     re-focused, because that would feel broken and cost conversions. */
  var keyboardMode = false;
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') keyboardMode = true;
  }, true);
  document.addEventListener('mousedown', function () { keyboardMode = false; }, true);
  document.addEventListener('touchstart', function () { keyboardMode = false; }, true);

  function visibleFocusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapFocus(container, returnTo) {
    function onKeydown(e) {
      if (e.key === 'Escape') { if (returnTo && returnTo.focus) returnTo.focus(); return; }
      if (e.key !== 'Tab') return;
      var items = visibleFocusables(container);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKeydown);
    var items = visibleFocusables(container);
    if (items.length) items[0].focus();
    else { container.setAttribute('tabindex', '-1'); container.focus(); }
    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (returnTo && typeof returnTo.focus === 'function') returnTo.focus();
    };
  }

  /* Give a non-button element button behaviour without touching its handler. */
  function makeOperable(el, label) {
    if (!el || el.dataset.adaOperable) return;
    el.dataset.adaOperable = '1';
    if (!el.getAttribute('role')) el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    if (label && !el.getAttribute('aria-label') && !el.textContent.trim()) el.setAttribute('aria-label', label);
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); el.click(); }
    });
  }

  /* ---- 1, 2, 3: video carousel ----------------------------------------- */
  function patchVideoCarousel() {
    var section = document.querySelector('.hs-video-section');
    if (!section) return;
    var cards = section.querySelectorAll('.hs-video-card');
    var dots = section.querySelectorAll('.hs-carousel-dots .hs-dot');
    var track = section.querySelector('.hs-carousel-track');

    Array.prototype.forEach.call(dots, function (dot, i) {
      var card = cards[i];
      var thumb = card && card.querySelector('.hs-video-thumbnail');
      var label = (thumb && thumb.dataset.label) ? thumb.dataset.label : '';
      dot.setAttribute('type', 'button');
      dot.setAttribute('aria-label', label
        ? 'Show video ' + (i + 1) + ' of ' + dots.length + ': ' + label
        : 'Show video ' + (i + 1) + ' of ' + dots.length);
      dot.setAttribute('aria-current', dot.classList.contains('active') ? 'true' : 'false');
    });

    if (dots.length && 'MutationObserver' in window) {
      var obs = new MutationObserver(function (records) {
        records.forEach(function (r) {
          if (r.attributeName !== 'class') return;
          r.target.setAttribute('aria-current', r.target.classList.contains('active') ? 'true' : 'false');
        });
      });
      Array.prototype.forEach.call(dots, function (d) { obs.observe(d, { attributes: true, attributeFilter: ['class'] }); });
    }

    Array.prototype.forEach.call(cards, function (card, i) {
      var thumb = card.querySelector('.hs-video-thumbnail');
      var label = (thumb && thumb.dataset.label) ? thumb.dataset.label : 'video ' + (i + 1);
      card.setAttribute('aria-label', 'Play ' + label);
      makeOperable(card);
    });

    if (track) {
      track.setAttribute('tabindex', '0');
      track.setAttribute('role', 'region');
      track.setAttribute('aria-label', 'Video carousel');
      track.addEventListener('keydown', function (e) {
        if (e.target !== track) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); track.scrollBy({ left: 220, behavior: 'smooth' }); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); track.scrollBy({ left: -220, behavior: 'smooth' }); }
      });
    }
  }

  /* ---- 4 + 10: modals and popups as real dialogs ------------------------ */
  /* Watches a class flag flipping to decide open/closed. Purely additive. */
  function watchDialog(node, opts) {
    if (!node || node.dataset.adaDialog) return;
    node.dataset.adaDialog = '1';
    var openClass = opts.openClass || 'active';
    var name = opts.label || 'Dialog';
    var titleEl = opts.titleSelector ? node.querySelector(opts.titleSelector) : null;

    node.setAttribute('role', 'dialog');
    node.setAttribute('aria-modal', 'true');
    if (titleEl) {
      if (!titleEl.id) titleEl.id = (node.id || 'ada-dlg') + '-ada-title';
      node.setAttribute('aria-labelledby', titleEl.id);
    } else if (!node.getAttribute('aria-label')) {
      node.setAttribute('aria-label', name);
    }

    var release = null;

    function sync() {
      var isOpen = node.classList.contains(openClass);
      if (isOpen && !release) {
        var o = (lastOpener && lastOpener.focus && !node.contains(lastOpener)) ? lastOpener : null;
        var oSel = o ? selectorFor(o) : null;
        var back = {
          focus: function () {
            var target = (o && document.contains(o)) ? o : null;
            if (!target && oSel) { try { target = document.querySelector(oSel); } catch (err) { target = null; } }
            if (target && typeof target.focus === 'function') target.focus();
          }
        };
        release = trapFocus(node, o ? back : null);
      } else if (!isOpen && release) {
        release(); release = null;
      }
    }

    if (!('MutationObserver' in window)) return;
    new MutationObserver(sync).observe(node, { attributes: true, attributeFilter: ['class'] });
    // Builders re-render their popup markup; a fresh node may arrive already open.
    if (node.classList.contains(openClass)) sync();
  }

  /* Who opened the dialog — tracked once, shared by every watched dialog. */
  var lastOpener = null;
  document.addEventListener('mousedown', function (e) { lastOpener = e.target; }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') lastOpener = e.target;
  }, true);

  var BUILDER_POPUPS = [
    { id: 'hs-mystery-notification',      label: 'Mystery scent unlocked', title: '.hs-mystery-title' },
    { id: 'hs-sept-mystery-notification', label: 'Free scent unlocked',    title: '#hs-sept-mystery-title' },
    { id: 'hsus-pop',                     label: 'Bundle options',         title: '.hsus-pop-t' },
    { id: 'hs30-pop',                     label: 'Bundle options',         title: '.hs30-pop-t' }
  ];

  /* Safe to call repeatedly: watchDialog skips nodes it has already seen,
     so this re-attaches only to popup markup a builder has just rebuilt. */
  function patchDialogs() {
    watchDialog(document.getElementById('hsVideoModal'), { openClass: 'active', label: 'Video player', titleSelector: '#hsModalLabel' });
    BUILDER_POPUPS.forEach(function (d) {
      watchDialog(document.getElementById(d.id), { openClass: 'show', label: d.label, titleSelector: d.title });
    });
  }

  // Escape closes the bundle popups, which previously had no key handler at all.
  // Registered once, not per patchDialogs() call.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    BUILDER_POPUPS.forEach(function (d) {
      var n = document.getElementById(d.id);
      if (!n || !(n.classList.contains('show'))) return;
      var btn = n.querySelector('[class*="close"], [class*="-x"], [class*="skip"], button');
      if (btn) btn.click();
    });
  });

  /* ---- 5: announcement bar marquee pause control ------------------------ */
  function patchMarquee() {
    Array.prototype.forEach.call(document.querySelectorAll('.announcement_bar__marquee'), function (marquee) {
      if (marquee.querySelector('.marquee-pause-button')) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'marquee-pause-button';
      btn.setAttribute('aria-label', 'Pause scrolling announcements');
      btn.innerHTML =
        '<svg class="mpb-pause" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
          '<rect x="2" y="1" width="3" height="10" rx="1"></rect><rect x="7" y="1" width="3" height="10" rx="1"></rect>' +
        '</svg>' +
        '<svg class="mpb-play" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
          '<polygon points="3,1 11,6 3,11"></polygon>' +
        '</svg>';
      btn.addEventListener('click', function () {
        var paused = marquee.classList.toggle('marquee--paused');
        btn.setAttribute('aria-label', paused ? 'Resume scrolling announcements' : 'Pause scrolling announcements');
      });
      marquee.appendChild(btn);
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        marquee.classList.add('marquee--paused');
        btn.setAttribute('aria-label', 'Resume scrolling announcements');
      }
    });
  }

  /* ---- 6: nav and cart drawers ------------------------------------------ */
  function patchDrawers() {
    [{ sel: 'header-drawer .drawer__content--nav, .header__drawer .drawer__content', name: 'Menu' },
     { sel: 'cart-drawer, .cart-drawer, #CartDrawer', name: 'Shopping cart' }].forEach(function (entry) {
      Array.prototype.forEach.call(document.querySelectorAll(entry.sel), function (node) {
        if (!node.getAttribute('role')) node.setAttribute('role', 'dialog');
        node.setAttribute('aria-modal', 'true');
        if (!node.getAttribute('aria-label') && !node.getAttribute('aria-labelledby')) node.setAttribute('aria-label', entry.name);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('.drawer.is-open, .drawer--open, cart-drawer.active, .cart-drawer.active');
      if (!open) return;
      var close = open.querySelector('.drawer__button-close, [class*="close"]');
      if (close) close.click();
    });
  }

  /* ---- 7: iframes -------------------------------------------------------- */
  function patchIframes() {
    Array.prototype.forEach.call(document.querySelectorAll('iframe:not([title])'), function (frame) {
      var src = frame.getAttribute('src') || '';
      if (src.indexOf('web-pixels') > -1 || src.indexOf('preview-bar') > -1) {
        frame.setAttribute('title', 'Analytics');
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
      } else {
        frame.setAttribute('title', 'Embedded content');
      }
    });
  }

  /* ---- 8: rebuild survival ----------------------------------------------
     Both bundle builders and the collection grid replace their whole subtree
     via innerHTML on every interaction. Two consequences this fixes:
       (a) keyboard focus is dumped to <body> after every pick / filter
       (b) every aria-live region is destroyed and recreated, so it never
           announces (a live region must pre-exist the change to fire)
     We keep one persistent live region OUTSIDE the rebuilt subtree and mirror
     the (otherwise dead) in-subtree status text into it.                     */
  var REBUILD_ZONES = '#hs-bundle-wrap, #hs-sept-wrap, [id^="hsus-wrap"], [id^="hs30-wrap"], #ProductGridContainer, #product-grid';
  var KEY_ATTRS = ['data-add', 'data-rem', 'data-variant', 'data-slot-remove', 'data-jump', 'data-case', 'data-case-id', 'data-go', 'data-bundle', 'data-step', 'data-filter', 'data-scroll', 'data-index', 'data-value', 'name', 'id'];

  function liveRegion() {
    var live = document.getElementById('ada-live-region');
    if (!live) {
      live = document.createElement('div');
      live.id = 'ada-live-region';
      live.className = 'ada-visually-hidden';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      document.body.appendChild(live);
    }
    return live;
  }

  function selectorFor(el) {
    for (var i = 0; i < KEY_ATTRS.length; i++) {
      var a = KEY_ATTRS[i];
      if (el.hasAttribute && el.hasAttribute(a)) {
        var v = el.getAttribute(a);
        if (v) return '[' + a + '="' + v.replace(/["\\]/g, '\\$&') + '"]';
      }
    }
    return null;
  }

  function patchRebuildZones() {
    var zones = document.querySelectorAll(REBUILD_ZONES);
    if (!zones.length || !('MutationObserver' in window)) return;
    var live = liveRegion();
    var pending = null;
    var lastMsg = '';

    // Remember which control the user activated, before the rebuild wipes it.
    function remember(e) {
      var t = e.target && e.target.closest
        ? e.target.closest('button, [role="button"], input, a[href], [data-add], [data-rem], [data-slot-remove], [data-jump], [data-bundle]')
        : null;
      if (!t) return;
      var zone = t.closest(REBUILD_ZONES);
      if (!zone) return;
      pending = selectorFor(t);
    }
    document.addEventListener('click', remember, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') remember(e);
    }, true);

    Array.prototype.forEach.call(zones, function (zone) {
      var t = null;
      new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(function () {
          // (a) mirror the dead status text into the persistent region
          var parts = [];
          Array.prototype.forEach.call(zone.querySelectorAll('[aria-live], [role="status"]'), function (n) {
            var txt = (n.textContent || '').replace(/\s+/g, ' ').trim();
            // Skip per-card stock badges ("Only 9 left"): with dozens of cards
            // they drowned out the one message that matters (count + total).
            if (/^only \d+ left$/i.test(txt) || /^\d+$/.test(txt)) return;
            if (txt) parts.push(txt);
          });
          var msg = parts.join('. ');
          if (msg && msg !== lastMsg) { lastMsg = msg; live.textContent = msg; }

          // (b) put keyboard focus back where the user left it
          if (keyboardMode && pending) {
            var el = null;
            try { el = zone.querySelector(pending); } catch (err) { el = null; }
            if (el && typeof el.focus === 'function' &&
                (document.activeElement === document.body || document.activeElement === null)) {
              el.focus();
            }
            pending = null;
          }

          // re-apply attribute patches to the freshly rebuilt markup
          patchDialogs();
          patchBundleControls();
          patchCollection();
          patchStyleLeak();
        }, 60);
      }).observe(zone, { childList: true, subtree: true });
    });
  }

  /* ---- 9: bundle builder controls ---------------------------------------- */
  /* Recover a scent name from the builder's own "Add <name>" control. */
  function nameForVariant(vid) {
    if (!vid) return '';
    var v = String(vid).replace(/["\\]/g, '');
    var el = document.querySelector('[data-add="' + v + '"][aria-label], [data-variant="' + v + '"][aria-label]');
    var lbl = el ? el.getAttribute('aria-label') : '';
    return lbl.replace(/^(Add another|Add|Remove one)\s+/i, '').replace(/\s+to bundle$/i, '').trim();
  }

  function patchBundleControls() {
    // "See scents" disclosure on pre-made bundles: a bare div, unreachable.
    Array.prototype.forEach.call(document.querySelectorAll('.hs-premade-toggle'), function (el) {
      makeOperable(el);
      var key = el.getAttribute('data-bundle');
      var pills = key ? document.querySelector('.hs-premade-pills[data-bundle="' + key + '"]') : null;
      var expanded = pills ? (getComputedStyle(pills).display !== 'none') : false;
      el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    // Remove-from-bundle buttons named only with a glyph.
    Array.prototype.forEach.call(
      document.querySelectorAll('.hs-slot-x, .hs-desktop-slot-x, .hsus-slot-x, .hs30-slot-x, [class*="slot-x"], [data-slot-remove]'), function (btn) {
        if (btn.getAttribute('aria-label') && btn.getAttribute('aria-label') !== 'Remove') return;
        // Start from the parent: the button's own class contains "slot".
        var parent = btn.parentElement;
        var slot = (parent && parent.closest('[class*="slot"]')) || parent;
        var img = slot ? slot.querySelector('img[alt]') : null;
        var nm = img && img.getAttribute('alt') ? img.getAttribute('alt').trim() : '';
        if (!nm) nm = nameForVariant(btn.getAttribute('data-rem') || btn.getAttribute('data-slot-remove'));
        btn.setAttribute('aria-label', nm ? 'Remove ' + nm + ' from your bundle' : 'Remove this item from your bundle');
      });

    // Pre-made bundle cards: every button read "SELECT THESE", with no bundle
    // named. Keep the visible words first in the name (2.5.3 Label in Name).
    Array.prototype.forEach.call(document.querySelectorAll('.hs-premade-select-btn'), function (b) {
      if (b.getAttribute('aria-label')) return;
      var card = b.closest('.hs-premade-card');
      var t = card ? card.querySelector('.hs-premade-card-title') : null;
      var nm = t ? (t.textContent || '').replace(/\s+/g, ' ').replace(/^[^A-Za-z0-9]+/, '').trim() : '';
      if (nm) b.setAttribute('aria-label', 'Select these: ' + nm);
    });

    // Horizontal slot strip: keyboard users could not scroll to slots 4-7.
    ['hs-slots-scroll', 'hs-sept-slots-scroll'].forEach(function (id) {
      var strip = document.getElementById(id);
      if (strip && !strip.hasAttribute('tabindex')) {
        strip.setAttribute('tabindex', '0');
        strip.setAttribute('role', 'group');
        strip.setAttribute('aria-label', 'Bundle slots');
      }
    });

    // Two-step wizard: active state was a CSS class only.
    var steps = document.querySelectorAll('.hs-step-btn, .hsus-step');
    Array.prototype.forEach.call(steps, function (b) {
      if (b.classList.contains('active') || b.classList.contains('on')) b.setAttribute('aria-current', 'step');
      else b.removeAttribute('aria-current');
    });

    // Jump / category chips: active state was a CSS class only.
    Array.prototype.forEach.call(document.querySelectorAll('.hsus-jump-btn[data-jump]'), function (b) {
      if (b.classList.contains('on')) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });

    // Decorative emoji should not be announced.
    Array.prototype.forEach.call(document.querySelectorAll('.hs-mystery-emoji, .hs-loading-emoji'), function (n) {
      n.setAttribute('aria-hidden', 'true');
    });

    // Loading overlay text should be announced before the checkout redirect.
    Array.prototype.forEach.call(document.querySelectorAll('.hs-loading-scent, #hsus-load, #hs30-load'), function (n) {
      if (!n.getAttribute('role')) { n.setAttribute('role', 'status'); n.setAttribute('aria-live', 'polite'); }
    });

    // Section titles rendered as plain divs — give them heading semantics
    // without touching a single CSS rule.
    Array.prototype.forEach.call(document.querySelectorAll('.hs-premade-card-title, .hsus-name, .hsus-pop-t, .hs30-pop-t'), function (n) {
      if (!n.getAttribute('role')) { n.setAttribute('role', 'heading'); n.setAttribute('aria-level', '3'); }
    });
  }

  /* ---- 11: collection page and facets ------------------------------------ */
  function patchCollection() {
    // Product grid had no list semantics.
    var grid = document.getElementById('product-grid');
    if (grid && !grid.getAttribute('role')) {
      grid.setAttribute('role', 'list');
      Array.prototype.forEach.call(grid.querySelectorAll('product-card'), function (c) {
        if (!c.getAttribute('role')) c.setAttribute('role', 'listitem');
      });
    }

    // Card swatches: the real radio is display:none, so the only tab stop was
    // an empty div. CSS restores the input; here we drop the decoy tab stop.
    Array.prototype.forEach.call(document.querySelectorAll('.button--variant[tabindex="0"], .checkbox.facet-checkbox[tabindex="0"]'), function (el) {
      el.removeAttribute('tabindex');
    });

    // "Clear all" was a plain div — unreachable and unactivatable.
    Array.prototype.forEach.call(document.querySelectorAll('.active-facets__clear-button'), function (el) {
      if (el.tagName !== 'A' && el.tagName !== 'BUTTON') makeOperable(el, 'Clear all filters');
    });

    // Remove-filter controls are icon-only with no accessible name.
    Array.prototype.forEach.call(document.querySelectorAll('.active-facets__button-link'), function (el) {
      if (el.getAttribute('aria-label')) return;
      var wrap = el.closest('.active-facets__button, facet-remove') || el.parentElement;
      var txt = wrap ? (wrap.textContent || '').replace(/\s+/g, ' ').trim() : '';
      el.setAttribute('aria-label', txt ? 'Remove filter: ' + txt : 'Remove filter');
    });

    // Icon SVGs carry no title and were being announced as noise.
    Array.prototype.forEach.call(document.querySelectorAll('.svg-wrapper > svg:not([aria-hidden]):not([role="img"])'), function (svg) {
      if (!svg.querySelector('title')) svg.setAttribute('aria-hidden', 'true');
    });

    // Sort control announced only its current value, never "Sort by".
    Array.prototype.forEach.call(document.querySelectorAll('.custom-select__btn'), function (btn) {
      var host = btn.closest('[data-label]');
      var label = host ? host.getAttribute('data-label') : 'Sort by';
      var val = (btn.textContent || '').replace(/\s+/g, ' ').trim();
      btn.setAttribute('aria-label', (label || 'Sort by') + ': ' + val);
    });

    // Radiogroups need a name.
    Array.prototype.forEach.call(document.querySelectorAll('fieldset[role="radiogroup"]:not([aria-label]):not([aria-labelledby])'), function (fs) {
      var legend = fs.querySelector('legend');
      if (legend && legend.textContent.trim()) return;
      fs.setAttribute('aria-label', 'Choose an option');
    });
  }

  /* ---- 12: inline <style> leaking into links and headings ----------------
     card-product.liquid prints a <style> element inside the product card's
     <a>, so its CSS text became part of the link's accessible name. Moving
     the node to <head> preserves cascade order (the rules are identical and
     global), and removes it from the accessibility tree entirely.           */
  function patchStyleLeak() {
    Array.prototype.forEach.call(document.querySelectorAll('a style, h1 style, h2 style, h3 style, h4 style, button style'), function (s) {
      if (s.dataset.adaMoved) return;
      s.dataset.adaMoved = '1';
      document.head.appendChild(s);
    });
  }

  function init() {
    try { patchStyleLeak(); }     catch (e) {}
    try { patchVideoCarousel(); } catch (e) {}
    try { patchDialogs(); }       catch (e) {}
    try { patchMarquee(); }       catch (e) {}
    try { patchDrawers(); }       catch (e) {}
    try { patchIframes(); }       catch (e) {}
    try { patchBundleControls(); }catch (e) {}
    try { patchCollection(); }    catch (e) {}
    try { patchRebuildZones(); }  catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();